import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  encryptData, 
  decryptData, 
  setVaultKey as setCryptoVaultKey, 
  getVaultKey,
  clearVaultKey,
  isVaultUnlocked as checkVaultUnlocked 
} from '../utils/vaultCrypto';
import { encryptedApi } from '../utils/encryptedApi';
import {
  isBiometricAvailable,
  setupBiometricBackup,
  recoverWithBiometric,
  isBiometricBackupEnabled,
  disableBiometricBackup,
  setupSimpleBiometricBackup,
  recoverFromSimpleBackup
} from '../utils/biometricBackup';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VaultContext = createContext(null);

export const useVault = () => {
  const context = useContext(VaultContext);
  if (!context) {
    throw new Error('useVault must be used within a VaultProvider');
  }
  return context;
};

export const VaultProvider = ({ children }) => {
  const [vaultStatus, setVaultStatus] = useState('loading'); // loading, setup, locked, unlocked
  const [vaultSalt, setVaultSalt] = useState(null);
  const [vaultTestValue, setVaultTestValue] = useState(null);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());

  // Auto-lock timeout (15 minutes of inactivity)
  const AUTO_LOCK_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds

  // Check vault status on mount
  useEffect(() => {
    checkVaultStatus();
  }, []);

  // Auto-lock on inactivity
  useEffect(() => {
    if (vaultStatus !== 'unlocked') return;

    const checkInactivity = () => {
      const now = Date.now();
      if (now - lastActivityTime > AUTO_LOCK_TIMEOUT) {
        console.log('Auto-locking vault due to inactivity');
        lockVault();
      }
    };

    // Check every minute
    const interval = setInterval(checkInactivity, 60000);

    // Update activity on user interaction
    const updateActivity = () => setLastActivityTime(Date.now());
    
    window.addEventListener('click', updateActivity);
    window.addEventListener('keypress', updateActivity);
    window.addEventListener('scroll', updateActivity);
    window.addEventListener('touchstart', updateActivity);

    return () => {
      clearInterval(interval);
      window.removeEventListener('click', updateActivity);
      window.removeEventListener('keypress', updateActivity);
      window.removeEventListener('scroll', updateActivity);
      window.removeEventListener('touchstart', updateActivity);
    };
  }, [vaultStatus, lastActivityTime]);

  // Auto-lock when app goes to background (mobile)
  useEffect(() => {
    if (vaultStatus !== 'unlocked') return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Store the time when app went to background
        sessionStorage.setItem('vault_background_time', Date.now().toString());
      } else if (document.visibilityState === 'visible') {
        // Check how long the app was in background
        const backgroundTime = sessionStorage.getItem('vault_background_time');
        if (backgroundTime) {
          const elapsed = Date.now() - parseInt(backgroundTime);
          // If more than 5 minutes in background, lock the vault
          if (elapsed > 5 * 60 * 1000) {
            console.log('Auto-locking vault after returning from background');
            lockVault();
          }
          sessionStorage.removeItem('vault_background_time');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [vaultStatus]);

  const checkVaultStatus = async () => {
    const token = localStorage.getItem('sehatSetu_token');
    if (!token) {
      setVaultStatus('setup'); // No user logged in
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/vault/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.has_vault) {
        setVaultSalt(response.data.salt);
        setVaultTestValue(response.data.test_value);
        
        // Check if already unlocked in memory
        if (checkVaultUnlocked()) {
          setVaultStatus('unlocked');
        } else {
          setVaultStatus('locked');
        }
      } else {
        setVaultStatus('setup');
      }
    } catch (error) {
      console.error('Vault status check error:', error);
      setVaultStatus('setup');
    }
  };

  const setupVault = async (salt, key, testValue) => {
    const token = localStorage.getItem('sehatSetu_token');
    
    try {
      // Save vault info to backend
      await axios.post(`${API_URL}/vault/setup`, {
        salt,
        test_value: testValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setVaultSalt(salt);
      setVaultTestValue(testValue);
      setCryptoVaultKey(key);
      setVaultStatus('unlocked');
      
      return true;
    } catch (error) {
      console.error('Vault setup error:', error);
      throw error;
    }
  };

  const unlockVault = (key) => {
    setCryptoVaultKey(key);
    setVaultStatus('unlocked');
  };

  const lockVault = () => {
    clearVaultKey();
    setVaultStatus('locked');
  };

  const updateVaultPIN = async (newSalt, newTestValue, newKey) => {
    const token = localStorage.getItem('sehatSetu_token');
    
    try {
      await axios.put(`${API_URL}/vault/update`, {
        salt: newSalt,
        test_value: newTestValue
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setVaultSalt(newSalt);
      setVaultTestValue(newTestValue);
      setCryptoVaultKey(newKey);
      
      return true;
    } catch (error) {
      console.error('Vault update error:', error);
      throw error;
    }
  };

  // Encrypt data before sending to server
  const encrypt = useCallback(async (data) => {
    const key = getVaultKey();
    if (!key) throw new Error('Vault is locked');
    return encryptData(data, key);
  }, []);

  // Decrypt data received from server
  const decrypt = useCallback(async (encryptedData) => {
    const key = getVaultKey();
    if (!key) throw new Error('Vault is locked');
    return decryptData(encryptedData, key);
  }, []);

  // Batch decrypt multiple records
  const decryptRecords = useCallback(async (records) => {
    const key = getVaultKey();
    if (!key) throw new Error('Vault is locked');
    
    return Promise.all(
      records.map(async (record) => {
        if (record.encrypted_blob) {
          try {
            const decrypted = await decryptData(record.encrypted_blob, key);
            return { ...record, ...decrypted, encrypted_blob: undefined };
          } catch {
            return { ...record, _decryptionFailed: true };
          }
        }
        return record;
      })
    );
  }, []);

  // Biometric functions
  const checkBiometricSupport = useCallback(async () => {
    return await isBiometricAvailable();
  }, []);

  const enableBiometric = useCallback(async (pin) => {
    const userId = localStorage.getItem('sehatSetu_user_id') || 'default_user';
    
    // Try WebAuthn first, fall back to simple backup
    const biometricSupported = await isBiometricAvailable();
    
    if (biometricSupported) {
      const result = await setupBiometricBackup(userId, vaultSalt, pin);
      return result;
    } else {
      // Fall back to simple storage (less secure but works everywhere)
      const result = setupSimpleBiometricBackup(vaultSalt, pin);
      return result;
    }
  }, [vaultSalt]);

  const unlockWithBiometric = useCallback(async () => {
    // Try WebAuthn first
    const biometricSupported = await isBiometricAvailable();
    
    if (biometricSupported) {
      const result = await recoverWithBiometric();
      if (result.success && result.data) {
        // Use the recovered PIN to unlock
        const { unlockVault: unlockVaultCrypto } = await import('../utils/vaultCrypto');
        const key = await unlockVaultCrypto(result.data.pin, result.data.salt);
        setCryptoVaultKey(key);
        setVaultStatus('unlocked');
        return { success: true };
      }
      return result;
    } else {
      // Try simple backup
      const result = recoverFromSimpleBackup();
      if (result.success && result.data) {
        const { unlockVault: unlockVaultCrypto } = await import('../utils/vaultCrypto');
        const key = await unlockVaultCrypto(result.data.pin, result.data.salt);
        setCryptoVaultKey(key);
        setVaultStatus('unlocked');
        return { success: true };
      }
      return result;
    }
  }, []);

  const hasBiometricEnabled = useCallback(() => {
    return isBiometricBackupEnabled();
  }, []);

  const disableBiometric = useCallback(() => {
    disableBiometricBackup();
  }, []);

  const value = {
    vaultStatus,
    vaultSalt,
    vaultTestValue,
    isUnlocked: vaultStatus === 'unlocked',
    needsSetup: vaultStatus === 'setup',
    isLocked: vaultStatus === 'locked',
    setupVault,
    unlockVault,
    lockVault,
    updateVaultPIN,
    encrypt,
    decrypt,
    decryptRecords,
    refreshStatus: checkVaultStatus,
    // Encrypted API
    api: encryptedApi,
    // Biometric support
    checkBiometricSupport,
    enableBiometric,
    unlockWithBiometric,
    hasBiometricEnabled,
    disableBiometric
  };

  return (
    <VaultContext.Provider value={value}>
      {children}
    </VaultContext.Provider>
  );
};

export default VaultContext;
